import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ContactPopup from './ContactPopup';
import { usePricing, PRICING_TABS } from '@/context/PricingContext';

// --- Process cards content (per tab) -------------------------------------

const PROCESS_CARDS = {
  [PRICING_TABS.SELF]: [
    {
      title: 'Brand Search',
      description: 'We provide the professional tools and data to help you conduct a thorough search, ensuring your brand identity is clear for filing.'
    },
    {
      title: 'Guided Preparation',
      description: 'Our system streamlines the filing process with expert prompts, ensuring your documentation meets all USPTO requirements accurately.'
    },
    {
      title: 'Compliance Tracking',
      description: 'We track your filing progress and offer timely support, helping you navigate official communications and post-submission requirements.'
    }
  ],
  [PRICING_TABS.ATTORNEY]: [
    {
      title: 'Application Submission',
      description: 'We gather your brand details and business information, ensuring every essential element is captured correctly for a strong legal foundation.'
    },
    {
      title: 'Attorney Review',
      description: 'Your dedicated counsel performs a legal risk assessment, expertly drafting your application to satisfy all USPTO standards and requirements.'
    },
    {
      title: 'Case Management',
      description: 'We handle the official electronic submission and manage all subsequent communications with the examining attorney to ensure seamless progress.'
    }
  ]
};

// --- Pricing section headers (per tab) ------------------------------------

const PRICING_HEADER = {
  [PRICING_TABS.SELF]: {
    title: 'Self-Filing with Expert Assistance',
    subtitle: 'Complete Support Plan for Easy Filing'
  },
  [PRICING_TABS.ATTORNEY]: {
    title: 'Attorney-Led Filing',
    subtitle: 'Complete End-to-End Service Plan'
  }
};

// --- Pricing datasets (independent per tab) -------------------------------

const SELF_FILING_PLANS = [
  {
    name: 'Basic',
    tagline: 'Simple guided process with expert support at every step to ensure accuracy',
    price: '$45',
    priceNote: 'Govt. filing fee not included',
    features: [
      'Case Review',
      'Case Filing',
      'Basic Search',
      'State E-Filing',
      'Trademark Secured'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Standard',
    popular: true,
    tagline: 'Balanced assistance that helps you move forward with clear professional direction',
    price: '$149',
    priceNote: 'Govt. filing fee not included',
    features: [
      'Case Review',
      'Case Filing',
      'Basic Trademark Search',
      'State + Federal E-Filing',
      'Trademark Alert (Basic)',
      'Digital File Access',
      'Case Status Updates',
      'Trademark Secured'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Deluxe',
    tagline: 'Advanced support with deeper planning, careful review, and ongoing clarity',
    price: '$395',
    priceNote: 'Govt. filing fee not included',
    features: [
      'Case Review',
      'Case Filing',
      'Advanced Direct Hit Search',
      'Federal & State Comprehensive Search Report',
      'Trademark Alert System',
      'Trademark Monitoring (Basic)',
      'Refusal Risk Check (Basic Level)',
      'Digital File + Documentation',
      'Dedicated Support (Email/Chat)',
      'Trademark Secured',
      '24 Hrs. Expedited Service'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Platinum',
    tagline: 'Premium guided service with expert attention, leadership, and complete confidence',
    price: '$595',
    priceNote: 'Govt. filing fee not included',
    features: [
      'Case Review',
      'Case Filing',
      'Direct Hit Search',
      'Trademark Alert',
      'Trademark Secured',
      '100% Satisfaction Guarantee',
      'Refusal Risk Assessment',
      'Trademark Monitoring',
      'Complete Documentation',
      'Digital File',
      'Dedicated Case Manager',
      '100% Approval Guarantee',
      'Comprehensive Trademark Search Report (Federal & State)',
      '24 Hrs. Expedited Service',
      'Cease & Desist Letter'
    ],
    cta: 'Get Started'
  }
];

const ATTORNEY_LED_PLANS = [
  {
    name: 'Basic',
    tagline: 'Reliable professional support for a smoother and more confident filing process',
    price: '$795',
    priceNote: 'Govt. filing fee not included',
    features: [
      'Attorney-Handled Trademark Filing from Start to Finish',
      'Professional Case Evaluation',
      'Application Preparation & Submission',
      'Preliminary Trademark Search',
      'State-Level E-Filing Included'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Standard',
    popular: true,
    tagline: 'Guided step-by-step assistance with added care, clarity, and peace of mind',
    price: '$895',
    priceNote: 'Govt. filing fee not included',
    features: [
      'Attorney-Led Trademark Filing',
      'End-to-End Case Handling',
      'Comprehensive Case Review & Analysis',
      'Professional Application Preparation',
      'Trademark Secured',
      'USPTO-Compliant Filing Process'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Deluxe',
    tagline: 'Organized service with expert review, careful preparation, and steady progress',
    price: '$1,145',
    priceNote: 'Govt. filing fee not included',
    features: [
      'Attorney-Led Case Review',
      'Complete Case Filing & Management',
      'Advanced Direct-Hit Trademark Search',
      'Trademark Monitoring & Alerts',
      'Comprehensive Risk & Refusal Meter'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Platinum',
    tagline: 'Priority support with focused attention, quicker handling, and greater confidence',
    price: '$1,545',
    priceNote: 'Government filing fee included',
    features: [
      'Attorney-Led Case Review & Strategy',
      'Complete Case Filing & Management',
      'Advanced Direct-Hit Trademark Search',
      'Federal & State Trademark Search Report',
      'Trademark Monitoring & Real-Time Alerts'
    ],
    cta: 'Get Started'
  }
];

const PLANS_BY_TAB = {
  [PRICING_TABS.SELF]: SELF_FILING_PLANS,
  [PRICING_TABS.ATTORNEY]: ATTORNEY_LED_PLANS
};

const TABS_ORDER = [PRICING_TABS.SELF, PRICING_TABS.ATTORNEY];

const TAB_LABELS = {
  [PRICING_TABS.SELF]: 'Self Filing with Assistance',
  [PRICING_TABS.ATTORNEY]: 'IP Attorney-Led Filing'
};

const PricingSection = () => {
  const { activePricingTab, setActivePricingTab, setSelectedPackage } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const plans = PLANS_BY_TAB[activePricingTab];
  const processCards = PROCESS_CARDS[activePricingTab];
  const header = PRICING_HEADER[activePricingTab];

  const handleSelectPackage = (plan) => {
    setSelectedPackage({ packageTitle: plan.name, packageAmount: plan.price });
    setIsPopupOpen(true);
  };

  const handleTabKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const currentIndex = TABS_ORDER.indexOf(activePricingTab);
    const nextIndex =
      e.key === 'ArrowRight'
        ? (currentIndex + 1) % TABS_ORDER.length
        : (currentIndex - 1 + TABS_ORDER.length) % TABS_ORDER.length;
    setActivePricingTab(TABS_ORDER[nextIndex]);
  };

  return (
    <>
      {/* Introduction Section */}
      <section className="py-20 bg-white" data-testid="pricing-intro-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Brand Protection Made Simple
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Trademark Protocols helps businesses secure their identity through reliable filing,
              monitoring, renewals, and complete trademark support. Whether your goal is to
              protect a company name, claim ownership of a logo, or safeguard a slogan, our
              team makes the process clear and manageable. We assist brands across all 50
              U.S. states and international markets, helping keep your intellectual property
              protected wherever your business operates.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Tabs */}
      <section className="pb-4 bg-white" data-testid="pricing-tabs-section">
        <div className="container mx-auto px-4">
          <div
            role="tablist"
            aria-label="Pricing Options"
            className="flex flex-col sm:flex-row justify-center gap-3 max-w-2xl mx-auto"
          >
            {TABS_ORDER.map((tab) => {
              const isActive = activePricingTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  id={`pricing-tab-${tab}`}
                  aria-selected={isActive}
                  aria-controls={`pricing-panel-${tab}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActivePricingTab(tab)}
                  onKeyDown={handleTabKeyDown}
                  className={`flex-1 px-6 py-4 rounded-full font-semibold text-sm md:text-base border-2 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 ${
                    isActive
                      ? 'bg-secondary-600 text-white border-secondary-600 shadow-lg scale-105'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-secondary-300'
                  }`}
                  data-testid={`pricing-tab-${tab}`}
                >
                  {TAB_LABELS[tab]}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Cards */}
      <section
        id={`pricing-panel-${activePricingTab}`}
        role="tabpanel"
        aria-labelledby={`pricing-tab-${activePricingTab}`}
        className="py-12 bg-gray-50"
        data-testid="pricing-process-section"
      >
        <div className="container mx-auto px-4">
          <div
            key={activePricingTab}
            className="grid md:grid-cols-3 gap-8 animate-fade-in-up"
          >
            {processCards.map((card, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 text-center card-hover"
                data-testid={`pricing-process-card-${activePricingTab}-${idx}`}
              >
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-gray-600 leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-primary-600" data-testid="pricing-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" key={`${activePricingTab}-header`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-2 text-white">{header.title}</h2>
            <p className="text-xl text-white/90">{header.subtitle}</p>
          </div>

          <div
            key={activePricingTab}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 animate-fade-in-up"
          >
            {plans.map((plan, idx) => (
              <div
                key={idx}
                className={`bg-white rounded-2xl p-8 shadow-lg relative flex flex-col ${
                  plan.popular ? 'ring-4 ring-primary-100 transform scale-105' : ''
                } card-hover`}
                data-testid={`pricing-plan-${activePricingTab}-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-secondary-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2 uppercase tracking-wide">{plan.name}</h3>
                  {plan.tagline && (
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{plan.tagline}</p>
                  )}
                  <div className="flex items-baseline justify-center mb-1">
                    <span className="text-4xl font-bold gradient-text">{plan.price}</span>
                  </div>
                  {plan.priceNote && (
                    <p className="text-xs text-gray-500">{plan.priceNote}</p>
                  )}
                </div>

                <div className="space-y-3 mb-6 flex-grow max-h-80 overflow-y-auto">
                  {plan.features.map((feature, featureIdx) => (
                    <div key={featureIdx} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#ff4d1b' }} />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => handleSelectPackage(plan)}
                  className={`w-full py-3 rounded-full font-semibold mt-auto ${
                    plan.popular
                      ? 'bg-secondary-600 text-white'
                      : 'bg-white text-primary-600 border-2 border-primary-600 hover:bg-primary-50'
                  }`}
                  data-testid={`pricing-cta-${activePricingTab}-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        pageSource={`pricing-${activePricingTab}`}
      />
    </>
  );
};

export default PricingSection;
